import React from "react";
import { PriceListSearchResultProps } from "../types";
import {FormattedMessage, useIntl} from "react-intl";
import Button from "@ui-components/Button";
import {useMediaQuery} from "@hooks/useMediaQuery";
import {splitCode} from "../../kcolor/utils/utils";
import {DisplayResa} from "../../kcolor/utils/DisplayResa";

export const PriceListSearchResult = ({
  priceList,
  onUpdatePriceList,
  onExit,
}: PriceListSearchResultProps) => {
  // hooks
  const intl = useIntl();
  const isWindowMoreThanLG = useMediaQuery("(min-width: 1024px)");
  // utils
  const color = `rgb(${priceList.color.rgb.join(", ")})`;
  const getContrastColor = (rgb: number[]): string => {
    // This function guarantees that the text is readable against the background color
    const [r, g, b] = rgb;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "black" : "white";
  };
  const data = [
    {label: {id: "product", defaultMessage: "Prodotto"}, value: priceList.kColorProduct.des_prod_comm},
    {label: {id: "product_family", defaultMessage: "Famiglia prodotto"}, value: priceList.kColorProduct.des_family},
    {label: {id: "package", defaultMessage: "Formato"}, value: `${priceList.productPackage} ${priceList.kColorProduct.base_um}`, three: true},
    ...(!!priceList.resa && !!priceList.um_resa
      ? [
          {
            label: {id: "sviluppo", defaultMessage: "Sviluppo"},
            value: <DisplayResa
              resa={`${Intl.NumberFormat(intl.locale,
                {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2
                }
              ).format(priceList.sviluppo || (priceList.productPackage / priceList.resa))} ${(priceList.um_sviluppo || 'm2')}`}/>,
            three: true
          },
          {
            label: {id: "resa", defaultMessage: "Resa"},
            value: <DisplayResa resa={`${priceList.resa} ${priceList.um_resa}`}/>,
            three: true
          },
        ]
      : []
    ),
  ];
  const renderPrice = (label: {id: string, defaultMessage: string}, value: number) => (
    <div className="flex flex-col items-start">
      <div className="text-xs text-gray-500 whitespace-nowrap">
        <FormattedMessage {...label} />
      </div>
      <div className={"flex flex-row items-baseline gap-1"}>
        <div className="font-medium text-xl">
          {Intl.NumberFormat(intl.locale, {style: "currency", currency: "EUR"}).format(value)}
        </div>
        <div>
          <span className={"text-xs text-gray-500"}>({Intl.NumberFormat(intl.locale, {style: "currency", currency: "EUR"}).format(value/priceList.productPackage)}/{priceList.um.toLowerCase()})</span>
        </div>
      </div>
    </div>
  );
  const hasTintometerPrice = ![null, undefined].includes(priceList.price.tintometer);

  return (
    <div className="flex flex-col lg:flex-row gap-x-4 gap-y-2">
      <div
        className="h-32 lg:w-1/3 lg:h-auto p-5 flex items-end select-none flex-shrink-0"
        style={{
          backgroundColor: color,
          ...(isWindowMoreThanLG ? {aspectRatio: "1 / 1"} : {}),
        }}
      >
        <h2 className="text-2xl lg:text-3xl" style={{color: getContrastColor(priceList.color.rgb)}}>
          {splitCode(priceList.color.code)}
        </h2>
      </div>
      <div className="flex flex-col justify-between flex-1">
        <div className="flex flex-col lg:flex-row gap-2 lg:gap-4">
          {!!priceList.kColorProduct.link_image && (
            <div
              className="flex items-center justify-center h-52 lg:w-1/4 lg:h-auto flex-shrink-0"
              style={{
                backgroundColor: "#efefef",
                ...(isWindowMoreThanLG ? {aspectRatio: "1 / 1"} : {}),
              }}
            >
              <img
                src={priceList.kColorProduct.link_image}
                alt={priceList.kColorProduct.des_prod_comm}
                className="h-full w-auto object-contain"
              />
            </div>
          )}
          <div className="grid grid-cols-2 flex-1 divide-y divide-gray-100">
            {data.map(({label, value, three}, index) => {
              const isThree = !!three && [-1, +1].some((delta) => !!data[index + delta]?.three);
              return (
                <div
                  key={index}
                  className={`py-3 flex flex-col justify-center ${isThree && isWindowMoreThanLG ? "col-span-1" : "col-span-3"}`}
                >
                  <p className="text-xs text-gray-500">
                    <FormattedMessage id={label.id} defaultMessage={label.defaultMessage}/>
                    {label.id === "product" && !!priceList.kColorProduct.link_scheda_tecnica && <>
                      {" ("}
                      <a
                        href={priceList.kColorProduct.link_scheda_tecnica}
                        target="_blank"
                        className="underline lowercase"
                      >
                        <FormattedMessage id="technical_data_sheet" defaultMessage="Scheda tecnica"/>
                      </a>
                      )
                    </>}
                  </p>
                  <div className={"flex flex-row gap-4"}>
                    <h2 className="text-xl">
                      {value}
                      {/*UNCOMMENT WHEN YOU WANT TO SHOW THE BASE*/}
                      {label.id === "product" && <span className="text-gray-500">
                        {" "}(base: {" "}
                        {priceList.tipo_base})
                      </span>
                      }
                    </h2>
                  </div>
                  {label.id === "product" && !!priceList.kColorProduct.abstract && (
                    <p
                      dangerouslySetInnerHTML={{__html: priceList.kColorProduct.abstract}}>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <hr className="border-gray-100"/>
          <div className="flex flex-col lg:flex-row gap-3 items-center justify-between">
            <div className={"flex flex-col w-full"}>
              <div className="flex flex-col lg:flex-row lg:items-center lg:gap-20 py-4 lg:py-0">
                {renderPrice(
                  {id: "public_price", defaultMessage: "Prezzo di Listino"},
                  priceList.price.kerakoll,
                )}
                {hasTintometerPrice && (
                  renderPrice({id: "price_tintometer", defaultMessage: "Prezzo tintometro"}, priceList.price.tintometer)
                )}
              </div>
              <div className={"flex flex-row items-center gap-8 lg:gap-3 py-4 lg:py-0"}>
                {intl.formatMessage({id: "price_excl_vat", defaultMessage: "Prezzi indicati escluso IVA, spese di trasporto e altri oneri"})}
              </div>
            </div>
            <div className="w-full flex flex-col lg:flex-row-reverse gap-x-3 gap-y-2">
              <Button full onClick={onUpdatePriceList}>
                <FormattedMessage id="edit" defaultMessage="Modifica"/>
              </Button>
              <Button styleType="white" full onClick={onExit}>
                <FormattedMessage id="close_kcolor_pricelist" defaultMessage="Chiudi"/>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
